import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { FAQS, getFaqBySlug } from "@/lib/faq";
import { authorForFaq } from "@/lib/authors";
import AuthorBadge from "@/components/blog/AuthorBadge";
import ShareButtons from "@/components/blog/ShareButtons";
import { useSeo, SITE_URL } from "@/lib/seo";

export default function EspertoRispondeDetail() {
  const { slug } = useParams();
  const faq = getFaqBySlug(slug);
  const author = faq ? authorForFaq(faq) : null;

  useSeo({
    title: faq ? `${faq.q} | Idea 360 Consulting` : "L'esperto risponde | Idea 360",
    description: faq ? faq.a.slice(0, 158) : "Risposte per imprenditori e startup.",
    path: `/esperto-risponde/${slug}`,
    jsonLd: faq
      ? {
          "@context": "https://schema.org",
          "@type": "QAPage",
          mainEntity: {
            "@type": "Question",
            name: faq.q,
            text: faq.q,
            answerCount: 1,
            acceptedAnswer: {
              "@type": "Answer",
              text: [faq.a, ...faq.detail].join(" "),
              url: `${SITE_URL}/esperto-risponde/${faq.slug}`,
              author: { "@type": "Person", name: author.name, jobTitle: author.role, sameAs: author.linkedin },
            },
          },
        }
      : null,
  });

  if (!faq) return <Navigate to="/blog" replace />;

  const related = FAQS.filter((f) => f.category === faq.category && f.slug !== faq.slug).slice(0, 3);
  const path = `/esperto-risponde/${faq.slug}`;

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-8 pt-16 pb-20 sm:pt-24">
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#FACC15] transition-colors mb-10"
        data-testid="detail-back-link"
      >
        <ArrowLeft size={15} />
        L'esperto risponde
      </Link>

      <p className="eyebrow mb-5">{faq.category}</p>
      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1]"
        style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
        data-testid="detail-h1"
      >
        {faq.q}
      </h1>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-y border-[#1a1a1a] py-5">
        <AuthorBadge author={author} className="sm:min-w-[320px]" />
        <ShareButtons path={path} title={faq.q} />
      </div>

      <div className="mt-8 border-l-2 border-[#FACC15] pl-6 py-1" data-testid="detail-short-answer">
        <p className="text-white text-lg leading-relaxed">{faq.a}</p>
      </div>

      {faq.detail.map((p, i) => (
        <p key={i} className="mt-6 text-white/70 text-base leading-relaxed">
          {p}
        </p>
      ))}

      <div className="mt-10 pt-6 border-t border-[#1a1a1a]">
        <ShareButtons path={path} title={faq.q} />
      </div>

      <div className="mt-12 border border-[#1a1a1a] bg-[#0c0c0c] p-7">
        <h2 className="text-xl text-white font-semibold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Vuoi una risposta sul tuo caso specifico?
        </h2>
        <p className="text-white/65 text-sm leading-relaxed mb-6">
          Ti rispondiamo entro 24 ore con una prima valutazione gratuita, senza impegno.
        </p>
        <Link to="/contatti" className="btn-yellow inline-flex items-center gap-2" data-testid="detail-cta">
          Richiedi una consulenza
          <ArrowRight size={18} />
        </Link>
      </div>

      {related.length > 0 && (
        <div className="mt-16">
          <p className="eyebrow mb-6">Domande correlate</p>
          <ul className="space-y-3">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to={`/esperto-risponde/${r.slug}`} className="text-white/80 hover:text-[#FACC15] transition-colors" data-testid={`related-${r.slug}`}>
                  {r.q}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}
